// frontend/src/pages/ProfilePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../utils/auth';
import { authenticatedRequest } from '../services/authApi';
import { REST_API_ORIGIN } from '../platform/endpoints';

const API_ORIGIN = REST_API_ORIGIN;




function buildInitialsFallback(name = 'U') {
  const safe = String(name || 'U').trim();
  const parts = safe.split(/\s+/).filter(Boolean);
  const initials = (parts.length >= 2
    ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`
    : safe.charAt(0) || 'U'
  ).toUpperCase();

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">
      <defs>
        <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#7C3AED"/>
          <stop offset="100%" stop-color="#2563EB"/>
        </linearGradient>
      </defs>
      <rect width="256" height="256" rx="128" fill="url(#g)"/>
      <circle cx="78" cy="70" r="70" fill="rgba(255,255,255,0.10)"/>
      <text x="50%" y="53%" text-anchor="middle" dominant-baseline="middle"
        font-family="Arial, Helvetica, sans-serif" font-size="96" font-weight="700" fill="#FFFFFF">${initials}</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function toPhotoURL(p, fallbackName = 'U') {
  if (!p) return buildInitialsFallback(fallbackName);
  if (/^data:image\//i.test(String(p))) return p;
  if (/^https?:\/\//i.test(String(p))) return p;

  const raw = String(p).replace(/^https?:\/\/[^/]+/i, '').trim();
  if (!raw) return buildInitialsFallback(fallbackName);

  if (raw.startsWith('/avatars/') || raw.startsWith('/uploads/')) {
    return `${API_ORIGIN}${raw}`;
  }

  let clean = raw.replace(/^\//, '');
  clean = clean.replace(/^uploads\//, '');
  if (!clean.startsWith('profile_photos/')) clean = `profile_photos/${clean}`;
  return `${API_ORIGIN}/${clean.split('/').map(encodeURIComponent).join('/')}`;
}

async function postJSON(url, body, token) {
  const res = await authenticatedRequest(url, {
    rawResponse: true,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body),
    credentials: 'include',
    diagSource: 'ProfilePage.postJSON',
  });
  return res;
}

async function fetchFirstOk(id, token) {
  const headers = {
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
  const opts = { method: 'GET', headers, credentials: 'include' };
  const url = `${API_ORIGIN}/api/users/by-id/${id}`;

  try {
    const res = await authenticatedRequest(url, { ...opts, rawResponse: true, diagSource: 'ProfilePage.fetchFirstOk' });
    if (res.status === 401) {
      return { error: '401', errorCode: 'UNAUTHORIZED', url };
    }
    if (res.status === 404) {
      return { error: '404', errorCode: 'NOT_FOUND', url };
    }
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      return {
        error: String(res.status),
        errorCode: 'HTTP_ERROR',
        status: res.status,
        message: txt,
        url,
      };
    }
    const data = await res.json();
    return { data, url };
  } catch (e) {
    return { error: 'NETWORK', errorCode: 'NETWORK', url, networkError: e };
  }
}

const SIGN_EMOJI = {
  aries: '♈', tauro: '♉', geminis: '♊', cancer: '♋', leo: '♌', virgo: '♍',
  libra: '♎', escorpio: '♏', sagitario: '♐', capricornio: '♑', acuario: '♒', piscis: '♓'
};
function normalizeSignKey(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
function getSignEmoji(sign) {
  if (!sign) return '';
  const key = normalizeSignKey(sign);
  return SIGN_EMOJI[key] || '';
}

const SIGN_TRANSLATION_KEYS = {
  aries: 'aries',
  tauro: 'taurus',
  taurus: 'taurus',
  geminis: 'gemini',
  gemini: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  scorpio: 'scorpio',
  sagitario: 'sagittarius',
  sagittarius: 'sagittarius',
  capricornio: 'capricorn',
  capricorn: 'capricorn',
  acuario: 'aquarius',
  aquarius: 'aquarius',
  piscis: 'pisces',
  pisces: 'pisces',
};

function getSignDisplayName(sign, t) {
  const translationKey = SIGN_TRANSLATION_KEYS[normalizeSignKey(sign)];
  return translationKey ? t(`home:zodiacSigns.${translationKey}`) : String(sign || '—');
}

function genderEmoji(g) {
  if (!g && g !== 0) return '';
  const key = String(g).toLowerCase();
  if (key === 'hombre' || key === 'male' || key === 'm') return '♂';
  if (key === 'mujer' || key === 'female' || key === 'f') return '♀';
  return '⚧';
}

export function resolveViewedProfileTarget(routeId, profile) {
  const routeTarget = String(routeId || '').trim();
  if (routeTarget) return routeTarget;
  return String(profile?._id || profile?.id || '').trim();
}

export default function ProfilePage() {
  const { t } = useTranslation('profile');
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    error: '',
    errorCode: '',
    errorStatus: null,
    user: null,
    usedUrl: '',
  });
  const [friendBusy, setFriendBusy] = useState(false);
  const [safetyBusy, setSafetyBusy] = useState(false);
  const [safetyState, setSafetyState] = useState({ blocked: false, notice: '', error: '' });
  const [friendState, setFriendState] = useState({
    isFriend: false,
    isPending: false,
    error: '',
    errorCode: '',
    notice: '',
    noticeCode: '',
  });

  const previousRoute = useMemo(() => {
    const directStateFrom = location.state && typeof location.state.from === 'string'
      ? String(location.state.from || '').trim()
      : '';
    if (directStateFrom) return directStateFrom;

    try {
      const referrer = typeof document !== 'undefined' ? String(document.referrer || '') : '';
      const sameOrigin = typeof window !== 'undefined' && referrer.startsWith(window.location.origin);
      if (sameOrigin) {
        const refUrl = new URL(referrer);
        const fallbackPath = `${refUrl.pathname || '/'}${refUrl.search || ''}${refUrl.hash || ''}`;
        if (fallbackPath && fallbackPath !== '/login') return fallbackPath;
      }
    } catch {}

    return '/feed';
  }, [location.state]);

  const handleGoBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      nav(-1);
      return;
    }
    nav(previousRoute, { replace: true });
  };

  useEffect(() => {
    const passed = location.state && location.state.user;
    if (passed) {
      setState(s => ({ ...s, loading: false, user: passed, error: '', errorCode: '' }));
      return;
    }
    try {
      const cache = JSON.parse(localStorage.getItem('lastRoomUsers') || '[]');
      const found = cache.find(u => String(u.id) === String(id));
      if (found) {
        setState(s => ({ ...s, loading: false, user: {
          _id: found.id,
          fullName: found.fullName,
          zodiacSign: found.zodiacSign,
          gender: found.gender,
          photoPath: found.photoPath
        }, error: '', errorCode: '' }));
      }
    } catch {}
  }, [id, location.state]);

  useEffect(() => {
    let abort = false;
    (async () => {
      const token = getAuthToken();
      const result = await fetchFirstOk(id, token);
      if (abort) return;

      if (result.data) {
        const user = result.data.user || result.data;
        setState({ loading: false, error: '', errorCode: '', errorStatus: null, user, usedUrl: result.url || '' });
        setFriendState({
          isFriend: !!user.isFriend,
          isPending: !!user.isPending,
          error: '',
          errorCode: '',
          notice: '',
          noticeCode: '',
        });
        setSafetyState((current) => ({ ...current, blocked: Boolean(user.blockedByMe) }));
        return;
      }
      if (result.error === '401') {
        setState({ loading: false, error: '', errorCode: 'UNAUTHORIZED', errorStatus: 401, user: null, usedUrl: '' });
        return;
      }
      setState({
        loading: false,
        error: result.message || '',
        errorCode: result.errorCode || (result.error ? 'HTTP_ERROR' : 'LOAD_FAILED'),
        errorStatus: result.status || Number(result.error) || null,
        user: null,
        usedUrl: result.url || '',
      });
    })();
    return () => { abort = true; };
  }, [id]);


  const { loading, error, errorCode, errorStatus, user, usedUrl } = state;
  const signEmoji = getSignEmoji(user?.zodiacSign);
  const signDisplayName = getSignDisplayName(user?.zodiacSign, t);
  const fallbackName = t('user.fallbackName');
  const visibleError = error || (
    errorCode === 'UNAUTHORIZED' ? t('errors.unauthorized')
      : errorCode === 'NOT_FOUND' ? t('errors.notFound')
      : errorCode === 'NETWORK' ? t('errors.network')
      : errorCode === 'HTTP_ERROR' ? t('errors.http', { status: errorStatus || '' })
      : t('errors.loadFailed')
  );

  const handleSendFriendRequest = async () => {
    if (!state.user || friendBusy || friendState.isFriend || friendState.isPending) return;
    setFriendBusy(true);
    setFriendState(s => ({ ...s, error: '', errorCode: '', notice: '', noticeCode: '' }));
    try {
      const token = getAuthToken();
      const res = await postJSON(`${API_ORIGIN}/api/contacts/request`, { toUserId: state.user._id }, token);
      if (res.status === 401) {
        setFriendState(s => ({ ...s, error: '', errorCode: 'UNAUTHORIZED' }));
        return;
      }

      let data = null;
      try { data = await res.json(); } catch { /* puede no ser JSON */ }

      // Caso amistoso: intentar agregarse a uno mismo → 200 OK con conflicto
      if (data && data.conflict === true && data.code === 'SELF_ADD') {
        setFriendState(s => ({ ...s, notice: '', noticeCode: 'SELF_ADD' }));
        return;
      }

      if (!res.ok) {
        const msg = (data && (data.message || data.error)) || `Error ${res.status}`;
        setFriendState(s => ({ ...s, error: msg }));
        return;
      }

      setFriendState(s => ({ ...s, isPending: true, notice: '', noticeCode: '' }));
    } catch {
      setFriendState(s => ({ ...s, error: '', errorCode: 'NETWORK' }));
    } finally {
      setFriendBusy(false);
    }
  };

  const handleBlockToggle = async () => {
    if (!state.user || safetyBusy) return;
    const targetUserId = resolveViewedProfileTarget(id, state.user);
    if (!targetUserId) {
      setSafetyState((current) => ({ ...current, error: t('safety.actionFailed') }));
      return;
    }
    const nextBlocked = !safetyState.blocked;
    if (nextBlocked && !window.confirm(t('safety.blockConfirm'))) return;
    setSafetyBusy(true);
    setSafetyState((current) => ({ ...current, notice: '', error: '' }));
    try {
      const response = await authenticatedRequest(`${API_ORIGIN}/api/contacts/blocks/${encodeURIComponent(targetUserId)}`, {
        method: nextBlocked ? 'PUT' : 'DELETE',
        rawResponse: true,
        credentials: 'include',
        diagSource: 'ProfilePage.safety.block',
      });
      if (!response.ok) throw new Error('block_failed');
      setSafetyState({ blocked: nextBlocked, notice: t(nextBlocked ? 'safety.blocked' : 'safety.unblocked'), error: '' });
      if (nextBlocked) setFriendState((current) => ({ ...current, isFriend: false, isPending: false }));
    } catch {
      setSafetyState((current) => ({ ...current, error: t('safety.actionFailed') }));
    } finally {
      setSafetyBusy(false);
    }
  };

  const handleReportUser = async () => {
    if (!state.user || safetyBusy) return;
    const reason = window.prompt(t('safety.reportPrompt'), 'harassment');
    if (!reason) return;
    const allowed = new Set(['harassment', 'hate', 'sexual_content', 'spam', 'impersonation', 'privacy', 'other']);
    if (!allowed.has(String(reason).trim())) {
      setSafetyState((current) => ({ ...current, error: t('safety.invalidReason') }));
      return;
    }
    setSafetyBusy(true);
    try {
      const response = await authenticatedRequest(`${API_ORIGIN}/api/contacts/reports/${state.user._id}`, {
        method: 'POST',
        rawResponse: true,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surface: 'profile', reason: String(reason).trim() }),
        diagSource: 'ProfilePage.safety.report',
      });
      if (!response.ok) throw new Error('report_failed');
      setSafetyState((current) => ({ ...current, notice: t('safety.reported'), error: '' }));
    } catch {
      setSafetyState((current) => ({ ...current, error: t('safety.actionFailed') }));
    } finally {
      setSafetyBusy(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin360 {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
        .profile-back-btn {
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1), filter 180ms ease, box-shadow 180ms ease;
        }
        .profile-back-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 32px rgba(0,0,0,0.34);
          filter: brightness(1.01);
        }
        .profile-back-btn:active {
          transform: translateZ(0) scale(0.97);
          filter: brightness(0.98);
        }
        .profile-shell {
          width: 100%;
          max-width: 1040px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .profile-hero {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }
        .profile-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(620px 260px at 4% 0%, rgba(255,215,135,0.035), rgba(255,215,135,0) 62%),
            radial-gradient(560px 280px at 96% 8%, rgba(90,36,135,0.035), rgba(90,36,135,0) 66%),
            linear-gradient(145deg, rgba(255,255,255,0.018), rgba(255,255,255,0) 48%);
          opacity: 0.42;
          z-index: 0;
        }
        .profile-hero > * {
          position: relative;
          z-index: 1;
        }
        .profile-hero::after {
          content: "";
          position: absolute;
          inset: auto 9% 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,224,151,0.42), transparent);
          opacity: 0.7;
          pointer-events: none;
        }
        .profile-identity {
          min-width: 0;
          flex: 1;
        }
        .profile-kicker {
          margin: 0 0 7px;
          color: rgba(255,221,146,0.8);
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }
        .profile-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 13px;
        }
        .profile-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        .profile-stat-card {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          border: 1px solid rgba(218,165,32,0.12);
          background: linear-gradient(145deg, rgba(13,2,24,0.97), rgba(4,0,10,0.98));
          box-shadow: 0 24px 48px rgba(0,0,0,0.52), 0 1px 0 rgba(255,255,255,0.02) inset;
          padding: 16px 15px 15px;
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }
        .profile-stat-card::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(
            135deg,
            rgba(218,165,32,0.025),
            rgba(255,255,255,0.002) 38%,
            rgba(75,0,130,0.025) 76%,
            rgba(218,165,32,0.012)
          );
          opacity: 0.5;
        }
        .profile-action-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }
        .profile-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 11px;
          border-radius: 999px;
          border: 1px solid rgba(218,165,32,0.13);
          background: linear-gradient(135deg, rgba(16,3,28,0.96), rgba(4,0,10,0.96));
          color: #ffedbb;
          font-size: 0.8rem;
          font-weight: 750;
          box-shadow: 0 12px 26px rgba(0,0,0,0.42), 0 1px 0 rgba(255,255,255,0.02) inset;
        }
        .profile-primary-btn, .profile-secondary-btn, .profile-danger-btn {
          -webkit-tap-highlight-color: transparent;
          transition: transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, filter 180ms ease, border-color 180ms ease;
        }
        .profile-primary-btn:hover:not(:disabled), .profile-secondary-btn:hover:not(:disabled), .profile-danger-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          filter: brightness(1.045);
        }
        .profile-primary-btn:hover:not(:disabled) { box-shadow: 0 16px 30px rgba(255,191,68,0.22), 0 12px 24px rgba(7,0,25,0.24) !important; }
        .profile-secondary-btn:hover:not(:disabled) { border-color: rgba(255,232,170,0.42) !important; }
        .profile-danger-btn:hover:not(:disabled) { border-color: rgba(255,152,173,0.66) !important; }
        .profile-primary-btn:active:not(:disabled), .profile-secondary-btn:active:not(:disabled), .profile-danger-btn:active:not(:disabled) { transform: scale(0.98); }
        .profile-section {
          position: relative;
          overflow: hidden;
        }
        .profile-section::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(120deg, rgba(255,255,255,0.018), transparent 44%);
        }
        @media (max-width: 760px) {
          .profile-shell {
            max-width: 100%;
          }
          .profile-stat-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }
          .profile-action-row {
            flex-direction: column;
            align-items: stretch;
          }
          .profile-chip-row { justify-content: center; }
        }
      `}</style>
      <style>{`
        /* Oculta la capa nítida estática del helper global para evitar doble imagen */
        .edge-blur-bg.profile::after { display: none !important; }

        /* Neutralizar cualquier rueda/blur heredado del helper global */
        .edge-blur-bg.profile::before {
          display: none !important;
          background: transparent !important;
          filter: none !important;
          opacity: 0 !important;
          content: "";
        }

        /* Fondo global dorado‑violeta (igual que el resto de las salas) */
        .edge-blur-bg.profile {
          background:
            radial-gradient(circle at 50% -10%, rgba(218,165,32,0.035), transparent 22%),
            radial-gradient(circle at 85% 35%, rgba(75,0,130,0.03), transparent 28%),
            linear-gradient(180deg, #030006 0%, #06000d 48%, #030006 100%);
          background-color: #030006;
        }

        /* Capa giratoria de la ruleta sobre el blur de fondo */
        .wheel-rotor {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .wheel-rotor::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100vmin;
          height: 100vmin;
          background-image: var(--bg-image-url);
          background-repeat: no-repeat;
          background-position: center;
          background-size: contain; /* rueda completa, sin recortes */
          transform: translate(-50%, -50%);
          will-change: transform;
          animation: spin360 40s linear infinite;
          filter: brightness(0.28) contrast(0.74) saturate(0.14) blur(2.6px);
          box-shadow: 0 0 120px rgba(0,0,0,0.66), inset 0 0 44px rgba(0,0,0,0.46);
          opacity: 0.06;
          background-color: transparent;
          border-radius: 50%;
          clip-path: circle(50% at 50% 50%);
        }
        @media (min-width: 1024px) {
          .wheel-rotor::before {
            width: 96vmin;
            height: 96vmin;
          }
        }
      `}</style>
      <style>{`
        /* Profile responsive tuning */
        .pf-header { display: flex; align-items: center; gap: 22px; }
        @media (max-width: 600px) {
          .pf-header { flex-direction: column; text-align: center; gap: 16px; }
          .profile-identity { width: 100%; }
        }
      `}</style>
      <div
        className="edge-blur-bg profile"
        style={{ '--bg-image-url': 'url("/logo/Zodiacus.jpeg")' }}
      >
        {/* Capa giratoria encima del blur global */}
        <div aria-hidden="true" className="wheel-rotor"></div>

        <div id="profileRoot" className="profile-shell" style={styles.page}>
          <button
            onClick={handleGoBack}
            className="profile-back-btn"
            style={styles.backBtn}
            aria-label={t('common:actions.back')}
          >
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '18px' }}>←</span>
            <span>{t('common:actions.back')}</span>
          </button>

          {loading && <p style={styles.muted}>{t('states.loading')}</p>}

          {!!(error || errorCode) && (
            <div style={styles.errorBox}>
              <strong>{t('errors.loadTitle')}</strong>
              <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{visibleError}</div>
              {usedUrl && process.env.NODE_ENV !== 'production' && <div style={{ marginTop: 6, opacity: 0.8 }}>{t('errors.lastAttempt')} <code>{usedUrl}</code></div>}
              <div style={{ marginTop: 10 }}>
                {errorCode === 'UNAUTHORIZED' || String(error || '').includes('401') ? (
                  <button onClick={() => nav('/login')} style={styles.loginBtn}>{t('actions.goToLogin')}</button>
                ) : (
                  <button onClick={handleGoBack} style={styles.loginBtn}>{t('common:actions.back')}</button>
                )}
              </div>
            </div>
          )}

          {user && (
            <div style={styles.card} className="profile-hero">
              <div style={styles.header} className="pf-header">
                <img
                  src={toPhotoURL(user.avatarUrl || user.photoPath || user.photo, user.fullName || user.username || fallbackName)}
                  alt={t('user.avatarAlt', { name: user.fullName || user.username || fallbackName })}
                  width={104}
                  height={104}
                  loading="lazy"
                  style={styles.avatar}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = buildInitialsFallback(user.fullName || user.username || fallbackName);
                  }}
                />
                <div className="profile-identity">
                  <div className="profile-kicker">{t('sections.publicPresence')}</div>
                  <h1 style={styles.name}>{user.fullName || user.username || fallbackName}</h1>
                  <p style={styles.sub}>
                    {signEmoji && (
                      <span aria-hidden style={styles.signEmoji}>{signEmoji}</span>
                    )}
                    <span style={styles.signText}>{signDisplayName}</span>
                    <span aria-hidden style={styles.sepDot}>·</span>
                    {typeof user.gender !== 'undefined' && (
                      <span style={styles.genderEmoji}>{genderEmoji(user.gender)}</span>
                    )}
                  </p>
                  <div className="profile-chip-row">
                    <span className="profile-chip">{t('stats.cosmicStatus')}: {friendState.isFriend ? t('stats.contact') : friendState.isPending ? t('stats.pending') : t('stats.available')}</span>
                    <span className="profile-chip">{signEmoji && <span aria-hidden>{signEmoji}</span>}{signDisplayName}</span>
                  </div>
                </div>
              </div>

              <div className="profile-stat-grid" style={{ marginTop: 16 }}>
                <div className="profile-stat-card">
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={styles.statLabel}>{t('stats.cosmicStatus')}</div>
                    <div style={styles.statValue}>{friendState.isFriend ? t('stats.contact') : friendState.isPending ? t('stats.pending') : t('stats.available')}</div>
                    <div style={styles.statHint}>{friendState.isFriend ? t('stats.connectedHint') : friendState.isPending ? t('stats.pendingHint') : t('stats.availableHint')}</div>
                  </div>
                </div>
                <div className="profile-stat-card">
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={styles.statLabel}>{t('stats.sign')}</div>
                    <div style={styles.statValue}>{signEmoji ? `${signEmoji} ${signDisplayName}` : signDisplayName}</div>
                    <div style={styles.statHint}>{t('stats.signHint')}</div>
                  </div>
                </div>
                <div className="profile-stat-card">
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={styles.statLabel}>{t('stats.profile')}</div>
                    <div style={styles.statValue}>{user.fullName || user.username || fallbackName}</div>
                    <div style={styles.statHint}>{typeof user.gender !== 'undefined' ? t('stats.gender', { gender: genderEmoji(user.gender) }) : t('stats.profileReady')}</div>
                  </div>
                </div>
              </div>

              <div style={styles.section} className="profile-section">
                <div style={styles.sectionHeaderRow}>
                  <h2 style={styles.h2}>{t('sections.bio')}</h2>
                  <span className="profile-chip">✨ {t('sections.publicPresence')}</span>
                </div>
                <p style={styles.text}>{user.bio || t('sections.emptyBio')}</p>
              </div>

              <div style={styles.section} className="profile-section">
                <div style={styles.sectionHeaderRow}>
                  <h2 style={styles.h2}>{t('sections.actions')}</h2>
                  <span className="profile-chip">🤝 {t('sections.cosmicConnections')}</span>
                </div>
                <div className="profile-action-row">
                  <button
                    className="profile-primary-btn"
                    style={{
                      ...styles.primary,
                      width: '100%',
                      maxWidth: 340,
                      opacity: friendBusy || friendState.isFriend ? 0.7 : 1,
                      cursor: friendBusy || friendState.isFriend ? 'not-allowed' : 'pointer'
                    }}
                    onClick={handleSendFriendRequest}
                    disabled={friendBusy || friendState.isFriend || friendState.isPending}
                  >
                    {friendState.isFriend ? t('friendRequest.alreadyFriends')
                      : friendState.isPending ? t('friendRequest.sent')
                      : friendBusy ? t('friendRequest.sending')
                      : t('friendRequest.send')}
                  </button>

                  {friendState.notice || friendState.noticeCode ? (
                    <span style={styles.noticeText}>
                      {friendState.noticeCode === 'SELF_ADD' ? t('friendRequest.selfAdd') : friendState.notice}
                    </span>
                  ) : null}
                  {friendState.error || friendState.errorCode ? (
                    <span style={styles.errorInlineText}>
                      {friendState.errorCode === 'UNAUTHORIZED'
                        ? t('errors.friendRequestUnauthorized')
                        : friendState.errorCode === 'NETWORK'
                          ? t('errors.friendRequestFailed')
                          : friendState.error}
                    </span>
                  ) : null}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 12 }}>
                    <button type="button" className="profile-secondary-btn" disabled={safetyBusy} onClick={handleReportUser} style={styles.secondary}>
                      {t('safety.report')}
                    </button>
                    <button type="button" className="profile-danger-btn" disabled={safetyBusy} onClick={handleBlockToggle} style={styles.danger}>
                      {t(safetyState.blocked ? 'safety.unblock' : 'safety.block')}
                    </button>
                  </div>
                  {safetyState.notice ? <span role="status" style={styles.noticeText}>{safetyState.notice}</span> : null}
                  {safetyState.error ? <span role="alert" style={styles.errorInlineText}>{safetyState.error}</span> : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const styles = {
  secondary: {
    padding: '11px 15px', borderRadius: 14, border: '1px solid rgba(255,232,170,0.12)',
    background: 'linear-gradient(135deg, rgba(14,2,24,0.97), rgba(3,0,9,0.97))', color: '#fff8e7', fontWeight: 750, cursor: 'pointer',
    boxShadow: '0 16px 32px rgba(0,0,0,0.48), 0 1px 0 rgba(255,255,255,0.02) inset',
  },
  danger: {
    padding: '11px 15px', borderRadius: 14, border: '1px solid rgba(255,137,158,0.42)',
    background: 'linear-gradient(135deg, rgba(70,10,31,0.84), rgba(24,3,16,0.9))', color: '#fff5f7', fontWeight: 800, cursor: 'pointer',
    boxShadow: '0 16px 32px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.018) inset',
  },
  page: {
    minHeight: '100dvh',
    padding: '20px 16px',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 76px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 36px)',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    fontFamily: "'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    isolation: 'isolate',
    background: 'transparent',
  },
  card: {
    maxWidth: 1000,
    margin: '22px auto',
    background: 'linear-gradient(145deg, rgba(10,1,18,0.985), rgba(3,0,9,0.985) 56%, rgba(2,0,7,0.99))',
    border: '1px solid rgba(218,165,32,0.12)',
    borderRadius: 30,
    padding: 'clamp(20px, 3vw, 34px)',
    boxShadow: '0 40px 100px rgba(0,0,0,0.76), 0 1px 0 rgba(255,255,255,0.025) inset, 0 0 80px rgba(218,165,32,0.015)',
    position: 'relative',
    zIndex: 1,
    backdropFilter: 'blur(26px) saturate(130%)',
    WebkitBackdropFilter: 'blur(26px) saturate(130%)'
  },
  backBtn: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
    left: 12,
    zIndex: 9999,

    // Touch target cómodo (iOS/Android)
    minHeight: 44,
    minWidth: 44,
    padding: '10px 14px',

    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,

    borderRadius: 999,
    border: '1px solid rgba(255,235,181,0.56)',
    background: 'linear-gradient(135deg, rgba(255,239,186,0.96), rgba(238,188,86,0.93))',
    color: '#321050',

    fontWeight: 900,
    letterSpacing: 0.2,

    boxShadow: '0 12px 30px rgba(4,0,21,0.34), 0 1px 0 rgba(255,255,255,0.36) inset',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',

    cursor: 'pointer',
    userSelect: 'none'
  },
  header: { display: 'flex', alignItems: 'center', gap: 22, marginBottom: 8, position: 'relative', zIndex: 1 },
  avatar: {
    width: 122,
    height: 122,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid rgba(255,229,157,0.92)',
    boxShadow: '0 18px 36px rgba(3,0,16,0.42), 0 0 0 7px rgba(255,220,129,0.09), 0 0 34px rgba(255,202,93,0.22), 0 1px 0 rgba(255,255,255,0.35) inset'
  },
  name: {
    margin: 0,
    fontSize: 'clamp(1.85rem, 3.6vw, 2.65rem)',
    fontWeight: 900,
    color: '#fff4cd',
    letterSpacing: '-0.035em',
    textShadow: '0 8px 24px rgba(0,0,0,0.32)'
  },
  sub: {
    margin: '7px 0 0',
    color: 'rgba(248,236,204,0.76)',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    gap: 6,
    lineHeight: 1.5,          // ↑ más alto para evitar recortes en iOS/Android
    paddingTop: 2,            // ↑ aire arriba
    paddingBottom: 2,         // ↑ aire abajo
    opacity: 1,
    fontSize: '0.94rem',
  },
  signText: { flexShrink: 0 },
  sepDot: { opacity: 0.75, display: 'inline-block', transform: 'translateY(-1px)' },
  genderEmoji: {
    display: 'inline-flex',              // caja propia
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.25em',                  // tamaño estable
    lineHeight: '1em',                   // altura acorde al glifo
    height: '1.25em',                    // asegura espacio vertical
    verticalAlign: 'middle',
    transform: 'none',                   // sin desplazamientos
    marginTop: 0,
    marginBottom: 0,
    overflow: 'visible',
    flexShrink: 0
  },
  signEmoji: {
    display: 'inline-block',
    fontSize: '1.25em',
    lineHeight: '1em',
    verticalAlign: 'middle',
    transform: 'translateY(0)',
    marginRight: 4,
    flexShrink: 0
  },
  section: {
    marginTop: 18,
    background: 'linear-gradient(145deg, rgba(11,2,20,0.97), rgba(3,0,9,0.97) 70%)',
    border: '1px solid rgba(218,165,32,0.11)',
    borderRadius: 22,
    padding: '18px clamp(16px, 2.5vw, 22px)',
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 24px 52px rgba(0,0,0,0.54), 0 1px 0 rgba(255,255,255,0.02) inset',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)'
  },
  h2: { margin: 0, fontSize: '1.02rem', color: '#ffdfa0', letterSpacing: '-0.015em', position: 'relative', zIndex: 1 },
  text: { margin: 0, color: 'rgba(249,242,224,0.8)', lineHeight: 1.75, fontSize: '0.98rem', position: 'relative', zIndex: 1 },
  muted: { color: '#ddd' },
  errorBox: {
    background: 'rgba(255,0,0,0.08)', border: '1px solid rgba(255,0,0,0.25)',
    padding: 12, borderRadius: 10, color: '#ffdada'
  },
  loginBtn: {
    background: 'transparent', color: '#FFD665', border: '1px solid #FFD665',
    borderRadius: 8, padding: '6px 10px', cursor: 'pointer'
  },
  primary: {
    background: 'linear-gradient(135deg, #fff0b8 0%, #f5ca6b 48%, #df9f45 100%)',
    color: '#321343',
    border: '1px solid rgba(255,238,185,0.68)',
    borderRadius: 16,
    padding: '13px 18px',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 12px 26px rgba(7,0,25,0.26), 0 1px 0 rgba(255,255,255,0.48) inset',
    transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, filter 180ms ease'
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  statLabel: {
    color: 'rgba(255,232,180,0.74)',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: 0.18,
    textTransform: 'uppercase',
  },
  statValue: {
    marginTop: 8,
    color: '#FFF3C4',
    fontSize: '1.08rem',
    fontWeight: 900,
    letterSpacing: -0.2,
  },
  statHint: {
    marginTop: 7,
    color: 'rgba(247,241,216,0.7)',
    fontSize: '0.86rem',
    lineHeight: 1.45,
  },
  noticeText: {
    color: '#FFD665',
    fontSize: '0.95rem',
    fontWeight: 700,
  },
  errorInlineText: {
    color: '#ffb3b3',
    fontSize: '0.9rem',
  },
};
